import { Col, Row } from "antd";
import { get, set } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineFreeCancellation, MdOutlinePayment } from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ANSET_SUBSCRIPTION_FORM_KEYS,
  SUBSCRIBER_TYPES,
} from "utils/constants";

const Subscriber = ({
  setPercent,
  isFieldRequired,
  setDisabled,
  initialValues,
  bindField,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const BLOCK = t("ANSET_SUBSCRIPTION.ADDITIONAL_INFORMATION", {
    returnObjects: true,
  });

  useEffect(() => {
    const subscriberType = get(
      initialValues,
      "additionalInfo.terminalUser.subscriberType",
    );
    const firstName = get(initialValues, "project.contact.user.firstname");
    const lastName = get(initialValues, "project.contact.user.lastname");

    if (subscriberType === SUBSCRIBER_TYPES.OTHER) return;

    if (
      firstName &&
      firstName !== initialValues?.additionalInfo?.terminalUser?.firstName
    ) {
      set(initialValues, "additionalInfo.terminalUser.firstName", firstName);
    }

    if (
      lastName &&
      lastName !== initialValues?.additionalInfo?.terminalUser?.lastName
    ) {
      set(initialValues, "additionalInfo.terminalUser.lastName", lastName);
    }
  }, [
    initialValues?.project?.contact?.user?.firstname,
    initialValues?.project?.contact?.user?.lastname,
  ]);

  const SUBSCRIBER_TYPE = {
    [SUBSCRIBER_TYPES.ADHERENT]: () => {
      set(initialValues, "additionalInfo.terminalUser", {
        firstName: get(initialValues, "project.contact.user.firstname"),
        lastName: get(initialValues, "project.contact.user.lastname"),
        subscriberType: SUBSCRIBER_TYPES.ADHERENT,
      });
      setDisabled((prev) => ({
        ...prev,
        additionalInfo: {
          ...prev?.additionalInfo,
          terminalUser: {
            ...prev?.additionalInfo?.terminalUser,
            firstName: true,
            lastName: true,
          },
        },
      }));
    },
    [SUBSCRIBER_TYPES.OTHER]: () => {
      set(initialValues, "additionalInfo.terminalUser", {
        firstName: null,
        lastName: null,
        subscriberType: SUBSCRIBER_TYPES.OTHER,
      });
      setDisabled((prev) => ({
        ...prev,
        additionalInfo: {
          ...prev?.additionalInfo,
          terminalUser: {
            ...prev?.additionalInfo?.terminalUser,
            firstName: false,
            lastName: false,
          },
        },
      }));
    },
  };

  const subscriberTypePath = "additionalInfo.terminalUser.subscriberType";
  const subscriberTypeField = bindField(subscriberTypePath, {
    label: BLOCK.SUBSCRIBER.FIELDS.SUBSCRIBER_TYPE.LABEL,
    required: isFieldRequired(subscriberTypePath),
    onChange: (value) => {
      SUBSCRIBER_TYPE[value]?.();
    },
    onBlur: () => setPercent(initialValues),
  });

  const firstNamePath = "additionalInfo.terminalUser.firstName";
  const firstNameField = bindField(firstNamePath, {
    label: BLOCK.SUBSCRIBER.FIELDS.FIRST_NAME.LABEL,
    required: isFieldRequired(firstNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const lastNamePath = "additionalInfo.terminalUser.lastName";
  const lastNameField = bindField(lastNamePath, {
    label: BLOCK.SUBSCRIBER.FIELDS.LAST_NAME.LABEL,
    required: isFieldRequired(lastNamePath),
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${ANSET_SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.SUBSCRIBER}-section`}
    >
      <Form
        type="vertical"
        title={BLOCK.SUBSCRIBER.TITLE}
        subtitle={BLOCK.SUBSCRIBER.SUBTITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={subscriberTypeField.path}
              label={subscriberTypeField.label}
              required={subscriberTypeField.required}
              disabled={subscriberTypeField.disabled}
            >
              {(field) => (
                <Input
                  type="radio"
                  variant="group"
                  icon={<MdOutlinePayment size={16} />}
                  label={subscriberTypeField.label}
                  values={Object.values(SUBSCRIBER_TYPES).map((value) => ({
                    value,
                    label:
                      BLOCK.SUBSCRIBER.FIELDS.SUBSCRIBER_TYPE.OPTIONS[value],
                  }))}
                  value={get(initialValues, subscriberTypePath)}
                  {...field}
                  onChange={subscriberTypeField.onChange}
                  onBlur={subscriberTypeField.onBlur}
                />
              )}
            </FormField>
          </Col>
          {get(initialValues, subscriberTypePath) ===
            SUBSCRIBER_TYPES.OTHER && (
            <Col lg={24} xs={24}>
              <Form type="highlight" title={BLOCK.SUBSCRIBER.FIELDS.LABEL}>
                <div className="grid--1">
                  <FormField
                    path={firstNameField.path}
                    label={firstNameField.label}
                    required={firstNameField.required}
                    disabled={firstNameField.disabled}
                  >
                    {(field) => (
                      <Input
                        placeholder={
                          BLOCK.SUBSCRIBER.FIELDS.FIRST_NAME.PLACEHOLDER
                        }
                        label={firstNameField.label}
                        icon={<MdOutlineFreeCancellation size={16} />}
                        defaultValue={firstNameField.defaultValue}
                        {...field}
                        onChange={firstNameField.onChange}
                        onBlur={firstNameField.onBlur}
                      />
                    )}
                  </FormField>
                  <FormField
                    path={lastNameField.path}
                    label={lastNameField.label}
                    required={lastNameField.required}
                    disabled={lastNameField.disabled}
                  >
                    {(field) => (
                      <Input
                        placeholder={
                          BLOCK.SUBSCRIBER.FIELDS.LAST_NAME.PLACEHOLDER
                        }
                        label={lastNameField.label}
                        icon={<MdOutlineFreeCancellation size={16} />}
                        defaultValue={lastNameField.defaultValue}
                        {...field}
                        onChange={lastNameField.onChange}
                        onBlur={lastNameField.onBlur}
                      />
                    )}
                  </FormField>
                </div>
              </Form>
            </Col>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default Subscriber;
